import { Instagram, Mail, Camera, Zap, Heart } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const ABOUT_IMG = "/images/behind-every-play.jpg";

const VALUES = [
  { icon: Camera, label: "Shot live, never staged" },
  { icon: Zap, label: "Fast, clean edits" },
  { icon: Heart, label: "Memories that last" },
];

export default function About() {
  return (
    <section id="about" className="relative mx-auto w-full max-w-7xl px-6 py-24 md:px-10 md:py-36">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <SectionHeading
            eyebrow="Behind the lens"
            title={
              <>
                FROM THE FENCE LINE,
                <br />
                <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                  FOR THE LOVE OF IT
                </span>
              </>
            }
          />

          <Reveal delay={0.15}>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-white/70">
              <p>
                GameLens Photography exists for the moments that vanish in a
                heartbeat — the release point, the full-extension slide, the
                dirt on the uniform and the grin after a big play.
              </p>
              <p>
                We shoot baseball the way it feels from the fence line: up
                close, high energy, and always chasing the frame you&rsquo;ll want
                on your wall twenty years from now. Action first, memories
                always.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <ul className="mt-8 space-y-4">
              {VALUES.map((v) => (
                <li key={v.label} className="flex items-center gap-4 text-sm font-semibold text-white/80">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface text-accentlight">
                    <v.icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  {v.label}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.35}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="https://instagram.com/gamelensphotos"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white/85 transition-all duration-300 hover:border-accentlight/60 hover:text-accentlight"
              >
                <Instagram className="h-4 w-4" /> @gamelensphotos
              </a>
              <a
                href="mailto:GameLensPhotos@gmail.com"
                data-testid="about-email-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white/85 transition-all duration-300 hover:border-accentlight/60 hover:text-accentlight"
              >
                <Mail className="h-4 w-4" /> GameLensPhotos@gmail.com
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-accent/40 via-accentlight/20 to-transparent blur-3xl"
            />
            <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]">
              <img
                src={ABOUT_IMG}
                alt="Player 55 sharing a laugh with a smiling teammate between plays"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-6 font-script text-3xl text-accentlight">
                Behind every great play.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
